const {
  ModalTest: { ResponseModal },
  Button,
} = window.Edocat.Components;

import StructureSet from './StructureSet';

import { onFormSubmit, makeAllFieldsDirty, highlightErrors, transitionSubmit } from '../../actions/form';

import prepareFormForSubmit from '../../lib/prepareFormForSubmit';
import { getBackUrl } from '../../lib/navigation';
import { validateForm, formDisabled } from '../../lib/validation';

import { FormJson, SetInterface } from 'edocatbase/Form';
import { ReduxComponentInterface } from 'edocatbase/Global';
import { Dispatch } from 'redux';

import { path } from 'ramda';

const getPathToForm = (formInstanceId: string, innerFormInstanceId?: string) => {
  let pathToForm: string[] = ['form'];
  if (innerFormInstanceId) {
    pathToForm = [formInstanceId, ...pathToForm, innerFormInstanceId];
  } else {
    pathToForm = [formInstanceId, ...pathToForm];
  }
  return pathToForm;
};

interface FormProps extends FormJson, ReduxComponentInterface {
  componentSuccessCallback?: (...args: any[]) => void;
  modals: { [key: string]: any };
  readOnly: boolean;
  showWFTemplateButton: boolean;
  onTemplateButtonClick: () => void;
}

class Form extends window.React.PureComponent<FormProps> {
  componentDidUpdate(prevProps: Readonly<FormProps>) {
    const { transitionSubmit } = this.props;
    if (!prevProps.transitionSubmit && transitionSubmit) {
      this.onFormSubmit();
    }
  }

  getFormResult = (withLabels = false) => {
    const { fields, sendForward, type, transitionSubmit, initialFields } = this.props;

    return {
      ...prepareFormForSubmit(fields, type, withLabels, transitionSubmit, initialFields),
      ...sendForward,
    };
  };


  validate() {
    const { fields, constraints } = this.props;

    const validation = validateForm(fields, constraints);
    this.highlightErrors(validation.validationErrors);

    return validation;
  }

  highlightErrors = (validationErrors: { [key: string]: string | boolean }) => {
    const { actions, formInstanceId, innerFormInstanceId } = this.props;

    actions.makeAllFieldsDirty(formInstanceId, innerFormInstanceId);
    actions.highlightErrors(formInstanceId, validationErrors, innerFormInstanceId);
  };

  successCallback = (response?: any) => {
    const { successCallback, propagateResponse, componentSuccessCallback } = this.props;
    if (successCallback) {
      if (propagateResponse) {
        successCallback(response);
      } else {
        successCallback();
      }
    } else if (componentSuccessCallback) {
      if (propagateResponse) {
        componentSuccessCallback(response);
      } else {
        componentSuccessCallback();
      }
    }
  };

  submit = async () => {
    const { actions, formInstanceId, submissionUrl, submissionUrlFieldValue, innerFormInstanceId, method, fields } = this.props;

    let data = this.getFormResult();
    if(formInstanceId === "playForm"){
      const everyKeys = Object.keys(fields);
      const keys = everyKeys.filter(item => item.startsWith("assoc_"));
      keys.forEach(key => {
        let jsonFieldAdded = data[`${key}_added`].split(",");
        let jsonFieldRemoved = data[`${key}_removed`].split(",");
        if(fields[key].value.length > 0){
          fields[key].value.forEach(item => jsonFieldAdded.concat(jsonFieldRemoved).includes(item?.value ?? item.nodeRef) ? "" : jsonFieldAdded.push(item?.value ?? item.nodeRef))
        }
        data[`${key}_added`] = jsonFieldAdded.toString();

      });
    }
    const submissionParamValue = submissionUrlFieldValue ? data[submissionUrlFieldValue] : '';
    const response = await actions.onFormSubmit(
      formInstanceId,
      data,
      `${submissionUrl}${submissionParamValue}`,
      method,
      innerFormInstanceId
    );

    if (response?.value.result === 'OK') {
      this.successCallback(response.value.response);
    }
  };

  onFormSubmit = (event?: React.FormEvent) => {
    event && event.preventDefault();

    const { actions, formInstanceId, innerFormInstanceId } = this.props;

    const { hasErrors } = this.validate();

    if (!hasErrors) {
      this.submit();
    } else {
      actions.transitionSubmit(formInstanceId, false, innerFormInstanceId);
    }
  };

  onCancelClick = (event: Event) => {
    event.preventDefault();
    const { urlAfterSubmit } = this.props;
    const backUrl = getBackUrl(urlAfterSubmit);
    window.location.assign(backUrl);
  };

  ignoreSubmit = (event: React.FormEvent) => {
    event.preventDefault();
  };

  onSaveAsTemplateClick = (event: Event) => {
    event.preventDefault();
    const { onTemplateButtonClick } = this.props;
    onTemplateButtonClick();
  }

  render() {
    const {
      autoComplete,
      structure,
      fields,
      backButtonAction,
      disabled,
      hideSubmitButtons,
      submitted,
      hideCancelButton,
      hideSubmitButton,
      submitButtonLabel,
      showWFTemplateButton,
      submittedButtonLabel,
      formInstanceId,
      innerFormInstanceId,
      modals,
      readOnly,
    } = this.props;

    const isAnyFormModalOpen =
      modals &&
      Object.keys(modals).some(it => {
        if (!it.startsWith('form')) return false;
        return modals[it]?.isOpen || false;
      });

    return (
      <div className={readOnly ? 'view-form' : 'edit-form'}>
        <form
          autoComplete={autoComplete || 'off'}
          onSubmit={!hideSubmitButtons && !isAnyFormModalOpen ? this.onFormSubmit : this.ignoreSubmit}
          noValidate
        >
          <div className="form-inner-wrap">
            {structure.map((set: SetInterface, index: number) => {
              return (
                <StructureSet
                  {...{ formInstanceId, innerFormInstanceId, set }}
                  key={set.id + index}
                  readOnly={readOnly}
                  disabled={disabled}
                />
              );
            })}
          </div>
          {!readOnly && (
            <window.React.Fragment>
              <div className={`form-button-wrap ${hideSubmitButtons ? 'hidden' : ''}`}>
                {!hideSubmitButton && (
                  <Button id="submitFormButton" primary>
                    <button type="submit" disabled={disabled || submitted || formDisabled(fields)}>
                      {submitted && submittedButtonLabel}
                      {!submitted && submitButtonLabel}
                      {disabled && <span className="spinner" />}
                      {submitted && <span className="submitted" />}
                    </button>
                  </Button>
                )}
                {showWFTemplateButton && (
                  <Button
                    id="saveAsTemplateButton"
                    label={window.Alfresco.messages.global['edocat.messages.workflow-template.save-button']}
                    onClick={this.onSaveAsTemplateClick}
                  />
                )}
                {!hideCancelButton && (
                  <Button
                    id="cancelFormButton"
                    label={window.Alfresco.messages.global['button.cancel']}
                    onClick={backButtonAction ? backButtonAction : this.onCancelClick}
                  />
                )}
              </div>
              <ResponseModal reducerPath={[formInstanceId || 'form', 'formModal', 'responseModal']} />
            </window.React.Fragment>
          )}
        </form>
      </div>
    );
  }
}

const mapStateToProps = (state: any, props: { formInstanceId: string; innerFormInstanceId?: string }) => {
  const pathToForm: string[] = getPathToForm(props.formInstanceId, props.innerFormInstanceId);

  return {
    ...(path(pathToForm, state) as { [key: string]: any }),
    modals: state[props.formInstanceId]?.formModal,
  };
};

const mapDispatchToProps = (dispatch: Dispatch) => ({
  actions: window.redux.bindActionCreators(
    { onFormSubmit, makeAllFieldsDirty, highlightErrors, transitionSubmit },
    dispatch
  ),
});

export default window.reactRedux.connect(mapStateToProps, mapDispatchToProps)(Form);
